import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import theme from './theme';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Products from './pages/Products';
import Categories from './pages/Categories';
import Suppliers from './pages/Suppliers';
import Inventory from './pages/Inventory';
import Recipes from './pages/Recipes';
import Reports from './pages/Reports';
import Users from './pages/Users';
import POS from './pages/POS';
import Warehouses from './pages/Warehouses';
import Customers from './pages/Customers';
import Antitheft from './pages/Antitheft';
import AuditLogs from './pages/AuditLogs';
import Units from './pages/Units';
import Stations from './pages/Stations';
import Tickets from './pages/Tickets';
import EstacionLive from './pages/EstacionLive';
import Comandas from './pages/Comandas';
import Caja from './pages/Caja';
import HistorialCajas from './pages/HistorialCajas';
import CuentasPorCobrar from './pages/CuentasPorCobrar';
import Tables from './pages/Tables';
import Facturacion from './pages/Facturacion';
import LoadingSpinner from './components/common/LoadingSpinner';

const PrivateRoute: React.FC<{ children: React.ReactNode; adminOnly?: boolean }> = ({
  children,
  adminOnly = false,
}) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) return <LoadingSpinner message="Verificando sesion..." />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (adminOnly && user?.role !== 'admin') return <Navigate to="/" replace />;

  return <>{children}</>;
};

// Los roles de estacion (bar/cocina) aterrizan directo en Tickets, no en el Dashboard.
const HomeRoute: React.FC = () => {
  const { user } = useAuth();
  if (user?.role === 'barman' || user?.role === 'cocinero') {
    return <Navigate to="/estacion" replace />;
  }
  if (user?.role === 'cajero') {
    return <Navigate to="/caja" replace />;
  }
  return <Dashboard />;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <LoadingSpinner message="Verificando sesion..." />;
  if (isAuthenticated) return <Navigate to="/" replace />;

  return <>{children}</>;
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Layout />
                </PrivateRoute>
              }
            >
              <Route index element={<HomeRoute />} />
              <Route path="pos" element={<POS />} />
              <Route path="productos" element={<Products />} />
              <Route path="categorias" element={<Categories />} />
              <Route path="unidades" element={<Units />} />
              <Route path="proveedores" element={<Suppliers />} />
              <Route path="movimientos" element={<Inventory />} />
              <Route path="recetas" element={<Recipes />} />
              <Route path="estaciones" element={<Stations />} />
              <Route path="estacion" element={<EstacionLive />} />
              <Route path="comandas" element={<Comandas />} />
              <Route path="caja" element={<Caja />} />
              <Route
                path="historial-cajas"
                element={
                  <PrivateRoute adminOnly>
                    <HistorialCajas />
                  </PrivateRoute>
                }
              />
              <Route path="cuentas-por-cobrar" element={<CuentasPorCobrar />} />
              <Route path="mesas" element={<Tables />} />
              <Route path="tickets" element={<Tickets />} />
              <Route path="bodegas" element={<Warehouses />} />
              <Route path="reportes" element={<Reports />} />
              <Route
                path="clientes"
                element={
                  <PrivateRoute adminOnly>
                    <Customers />
                  </PrivateRoute>
                }
              />
              <Route
                path="usuarios"
                element={
                  <PrivateRoute adminOnly>
                    <Users />
                  </PrivateRoute>
                }
              />
              <Route
                path="antirobo"
                element={
                  <PrivateRoute adminOnly>
                    <Antitheft />
                  </PrivateRoute>
                }
              />
              <Route
                path="auditoria"
                element={
                  <PrivateRoute adminOnly>
                    <AuditLogs />
                  </PrivateRoute>
                }
              />
              <Route
                path="facturacion"
                element={
                  <PrivateRoute adminOnly>
                    <Facturacion />
                  </PrivateRoute>
                }
              />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
};

export default App;
